import { Mic, Square, X } from 'lucide-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { type RecordingPhase, recordAudio } from '../lib/audioRecording';

type Props = {
  disabled?: boolean;
  isCurrent: () => boolean;
  onFile: (file: File) => void;
  onError: (message: string | null) => void;
  onBusyChange: (busy: boolean) => void;
};

export function AudioRecorder({ disabled, isCurrent, onFile, onError, onBusyChange }: Props) {
  const [phase, setPhase] = useState<RecordingPhase>('idle');
  const [seconds, setSeconds] = useState(0);
  const alive = useRef(true);
  const busy = useRef(false);
  const recording = useRef<ReturnType<typeof recordAudio> | null>(null);
  const busyCallback = useRef(onBusyChange);
  busyCallback.current = onBusyChange;

  useLayoutEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      recording.current?.cancel();
      busyCallback.current(false);
    };
  }, []);

  function start(): void {
    if (disabled || busy.current || !isCurrent()) return;
    onError(null);
    recording.current = recordAudio({
      isCurrent: () => alive.current && isCurrent(),
      onChange: (next, elapsed) => {
        busy.current = next !== 'idle';
        onBusyChange(busy.current);
        setPhase(next);
        setSeconds(elapsed);
      },
      onFile,
      onError,
    });
  }

  return (
    <div className={phase === 'idle' ? 'shrink-0' : 'min-w-0 flex-1'}>
      {phase === 'idle' ? (
        <button
          type="button"
          disabled={disabled}
          onClick={start}
          title="Записать аудио"
          aria-label="Записать аудио"
          className="mc-press flex h-11 w-11 items-center justify-center rounded-full text-foreground active:bg-subtle md:hover:bg-subtle disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Mic size={20} strokeWidth={1.8} aria-hidden="true" />
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <output className="w-full text-[13px] text-muted-foreground">
            {phase === 'starting'
              ? 'Разрешите доступ к микрофону…'
              : phase === 'stopping'
                ? 'Завершаем запись…'
                : 'Идёт запись · максимум 10 минут'}
          </output>
          {phase === 'recording' && (
            <span className="mc-tabular text-[14px]" role="timer" aria-label="Время записи">
              {Math.floor(seconds / 60)
                .toString()
                .padStart(2, '0')}
              :{(seconds % 60).toString().padStart(2, '0')}
            </span>
          )}
          <button
            type="button"
            disabled={phase !== 'recording'}
            onClick={() => recording.current?.stop()}
            className="mc-press flex min-h-11 items-center gap-2 rounded-full border border-border px-3 text-[13px] hover:bg-subtle disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Square size={14} aria-hidden="true" />
            Завершить
          </button>
          <button
            type="button"
            onClick={() => recording.current?.cancel()}
            className="mc-press flex min-h-11 items-center gap-2 rounded-full border border-border px-3 text-[13px] hover:bg-subtle"
          >
            <X size={16} aria-hidden="true" />
            Отменить
          </button>
        </div>
      )}
    </div>
  );
}
